import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { Placeholder } from '@tiptap/extensions/placeholder';

export default (content, inputRef, placeholder) => {
    let editor;

    return {
        content,
        inputRef,
        placeholder,
        updatedAt: Date.now(),

        init() {
            const _this = this;

            editor = new Editor({
                element: this.$refs[this.inputRef],
                extensions: [
                    StarterKit,
                    Markdown.configure({
                        html: false,
                        transformPastedText: true,
                        transformCopiedText: true,
                    }),
                    Placeholder.configure({
                        placeholder: this.placeholder ?? '',
                    }),
                ],
                content: this.content,
                editorProps: {
                    attributes: {
                        class: 'focus:outline-none',
                    },
                },
                onUpdate: ({ editor }) => {
                    this.content = editor.storage.markdown.getMarkdown();
                    _this.updatedAt = Date.now();
                },
                onSelectionUpdate() {
                    _this.updatedAt = Date.now();
                },
            });

            this.$watch('content', (content) => {
                if (content === editor.storage.markdown.getMarkdown()) return;
                editor.commands.setContent(content, false);
            });
        },

        // ── Active state ──────────────────────────────────────────────────

        // updatedAt is passed purely to trigger Alpine reactivity; it is ignored
        isActive(type, updatedAt) {
            return editor.isActive(type);
        },

        isHeadingActive(level, updatedAt) {
            return editor.isActive('heading', { level });
        },

        canUndo(updatedAt) {
            return editor.can().undo();
        },

        canRedo(updatedAt) {
            return editor.can().redo();
        },

        // ── Text style ────────────────────────────────────────────────────

        toggleBold() {
            editor.chain().focus().toggleBold().run();
        },

        toggleItalic() {
            editor.chain().focus().toggleItalic().run();
        },

        toggleStrike() {
            editor.chain().focus().toggleStrike().run();
        },

        toggleCode() {
            editor.chain().focus().toggleCode().run();
        },

        // ── Headings ──────────────────────────────────────────────────────

        toggleHeading(level) {
            editor.chain().focus().toggleHeading({ level }).run();
        },

        // ── Lists ─────────────────────────────────────────────────────────

        toggleBulletList() {
            editor.chain().focus().toggleBulletList().run();
        },

        toggleOrderedList() {
            editor.chain().focus().toggleOrderedList().run();
        },

        // ── Block elements ────────────────────────────────────────────────

        toggleBlockquote() {
            editor.chain().focus().toggleBlockquote().run();
        },

        toggleCodeBlock() {
            editor.chain().focus().toggleCodeBlock().run();
        },

        setHorizontalRule() {
            editor.chain().focus().setHorizontalRule().run();
        },

        // ── History ───────────────────────────────────────────────────────

        undo() {
            editor.chain().focus().undo().run();
        },

        redo() {
            editor.chain().focus().redo().run();
        },

        // ── Lifecycle ─────────────────────────────────────────────────────

        destroy() {
            editor?.destroy();
        },
    };
};
